import { Injectable } from '@angular/core';

import { FixtureStage } from '@cds/betting-offer';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { FavouritesConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { UserService } from '@frontend/sports/user/feature';
import { produce } from 'immer';
import { clone, flatMap, sumBy } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import {
    BaseTreeItem,
    GroupedEvents,
    LeftNavType,
    NavEventModel,
    SelectionInfo,
    TreeItem,
    TreeItemType,
} from '../../event-details-common/event-details-common.models';
import { getLeagueId } from '../../event-details-common/event-details-common.utils';
import { FixtureList } from '../../event-list-shared/sport/competitions/competition.models';
import { LiveFavouriteFixturesService } from '../../favourites/live-favourites-fixtures.service';
import { LiveUrlParam } from '../../navigation-core/url-helper.service';
import { NavigationTrackingService } from '../navigation-tracking/navigation-tracking.service';
import { LiveEventsNavigationStateService } from './live-events-navigation-state.service';
import { LiveEventsNavigationSubscriptionService } from './live-events-navigation-subscription.service';

export interface ToggleResult {
    leaguesToFetch: LeagueGroup[];
    tree: BaseTreeItem;
}

export interface LeagueGroup {
    leagueId: number;
    virtualLeagueId: number;
}

@Injectable({ providedIn: 'root' })
export class LiveEventsNavigationTreeService {
    private selectionInfo: SelectionInfo;
    favouritedLeagues: TreeItem[] = [];
    subscription?: EventSubscription<EventModel>;
    eventSubject = new BehaviorSubject<EventModel>(<EventModel>{});

    constructor(
        private stateService: LiveEventsNavigationStateService,
        private trackingService: NavigationTrackingService,
        private subscriptionsService: LiveEventsNavigationSubscriptionService,
        private sitecore: Sitecore,
        private liveFavouriteFixturesService: LiveFavouriteFixturesService,
        private favouritesConfig: FavouritesConfig,
        private userService: UserService,
        private fixtureFactory: FixtureFactory,
    ) {}

    get favEventChange(): BehaviorSubject<EventModel> {
        return this.eventSubject;
    }

    refreshFavouritesTreeItem(tree: BaseTreeItem): BaseTreeItem {
        const updatedChildren = this.updateFavouritedItemsLeftNavigation(tree).children.filter((sport) => sport.id !== LiveUrlParam.Favourites);
        if (this.favouritedLeagues.length > 0) {
            const favouritesTab = this.getFavouriteTab();

            return { children: [favouritesTab, ...updatedChildren] };
        }

        return { children: updatedChildren };
    }

    updateTree(navTree: BaseTreeItem, leagueGroups: LeagueGroup[], groupedEvents: GroupedEvents): BaseTreeItem {
        if (!groupedEvents) {
            return navTree;
        }

        const added: EventModel[] = [];

        let tree = produce(navTree, (draftState: BaseTreeItem) => {
            leagueGroups.forEach((leagueGroup) => {
                let sportIndex = -1;
                const leagueId = leagueGroup.virtualLeagueId > 0 ? leagueGroup.virtualLeagueId : leagueGroup.leagueId;
                const events = groupedEvents[leagueId];
                const anyEvents = events && events.length > 0;
                if (anyEvents) {
                    sportIndex = navTree.children.findIndex((s) => s.id === events[0].sport.id && s.id !== (LiveUrlParam.Favourites as number));
                }

                if (sportIndex < 0) {
                    // try to find based on league id, because it might happen no events are returned
                    sportIndex = navTree.children.findIndex((s) => s && s.children.some((l) => l.id === leagueGroup.leagueId));
                }

                if (sportIndex < 0) {
                    // if  matching sport could not be fount continue to the next leagueId
                    return;
                }

                const leagueIndex = draftState.children[sportIndex].children.findIndex(
                    (l) => l.id === leagueGroup.leagueId && l.type !== TreeItemType.FavouriteLeague,
                );
                if (leagueIndex < 0) {
                    // if matching league model could not be found continue to the next leagueId
                    return;
                }

                if (anyEvents) {
                    // subscribe to push notifications
                    added.push(...events);

                    // since navTree model is immutable, replace with an updated model
                    const sport = draftState.children[sportIndex];
                    const league = sport.children[leagueIndex];
                    league.events = events.map((e) => ({
                        data: e,
                        selected: e.id === this.selectionInfo.eventId,
                        showMarkets: sport.toggleMarkets,
                        hidden: false,
                    }));
                    league.loading = false;
                } else {
                    // remove league if there are no events
                    draftState.children[sportIndex].children.splice(leagueIndex, 1);
                }
            });
        });

        this.subscriptionsService.subscribe(added);

        if (this.favouritesConfig.isLiveFavouritesEnabled && this.userService.isAuthenticated) {
            tree = this.updateFavouritedItemsLeftNavigation(tree);
        }

        return tree;
    }

    private updateFavouritedItemsLeftNavigation(navTree: BaseTreeItem): BaseTreeItem {
        return produce(navTree, (draftState: BaseTreeItem) => {
            draftState.children.forEach((sport) => {
                if (sport.id !== LiveUrlParam.Favourites) {
                    let hiddenLeagueCount = 0;
                    sport.children.forEach((league) => {
                        let hiddenEventCount = 0;
                        league.events.forEach((event) => {
                            const favouritedFixture = this.liveFavouriteFixturesService.isFavourited(event.data.id);
                            event.hidden = false;
                            league.selected =
                                this.selectionInfo.leagueId === league.id && this.selectionInfo.eventId === event.data.id && !favouritedFixture;
                            if (favouritedFixture) {
                                event.hidden = true;
                                hiddenEventCount++;
                            }
                        });
                        league.hidden = false;
                        if (hiddenEventCount > 0 && hiddenEventCount === league.events.length) {
                            league.hidden = true;
                            hiddenLeagueCount++;
                        }
                    });
                    sport.hidden = false;
                    if (hiddenLeagueCount > 0 && hiddenLeagueCount === sport.children.length) {
                        sport.hidden = true;
                    }
                }
            });
        });
    }

    toggleMarkets(navTree: BaseTreeItem, sport: TreeItem): BaseTreeItem {
        this.trackingService.trackMarketsToggle(!sport.toggleMarkets, sport.leftNavType);

        return produce(navTree, (draft: BaseTreeItem) => {
            const draftSport = this.findChild(draft, sport.id);
            if (!draftSport) {
                return;
            }
            draftSport.toggleMarkets = !draftSport.toggleMarkets;
            this.stateService.toggleMarkets(sport.id);
            draftSport.children.forEach((league) => {
                league.events.forEach((event) => {
                    event.showMarkets = !event.showMarkets;
                });
            });
        });
    }

    getVisibleLeagues(navTree: BaseTreeItem): LeagueGroup[] {
        const leagues: LeagueGroup[] = [];
        navTree.children
            .filter((sport) => sport.expanded && sport.id !== LiveUrlParam.Favourites)
            .forEach((sport) => {
                sport.children.forEach((league) => {
                    if (league.expanded) {
                        leagues.push(this.getLeagueGroup(league.id, league.virtualCompetitionId || 0));
                    }
                });
            });

        return leagues;
    }

    expandEvent(navTree: BaseTreeItem, event: EventModel): ToggleResult {
        const leaguesToFetch: LeagueGroup[] = [];
        const newTree = produce(navTree, (draftState: BaseTreeItem) => {
            const currentSport = this.findChild(draftState, event.sport.id);
            if (!currentSport) {
                return;
            }
            const currentLeague = this.findChild(currentSport, getLeagueId(event));
            if (!currentLeague) {
                return;
            }
            if (!currentLeague.expanded) {
                currentLeague.expanded = true;
                this.stateService.toggleLeague(currentLeague.id);
            }
            if (!currentSport.expanded) {
                currentSport.expanded = true;
                this.stateService.toggleSport(currentSport.id);
            }
            currentSport.children.forEach((league) => {
                if (league.expanded) {
                    leaguesToFetch.push(this.getLeagueGroup(league.id, league.virtualCompetitionId || 0));
                }
            });
        });

        return {
            leaguesToFetch,
            tree: newTree,
        };
    }

    updateFavourites(tree?: BaseTreeItem): void {
        if (!tree) {
            return;
        }
        this.unsubscribeInvisibleEvents(tree);
        this.setFavouriteLeagues();

        const favouriteEvents = this.getFavouritesEvents();
        this.subscriptionsService.unsubscribe(favouriteEvents);
        this.subscriptionsService.subscribe(favouriteEvents);
    }

    private getFavouritesEvents(): EventModel[] {
        return flatMap(this.favouritedLeagues, (league) => league.events).map((event) => event.data);
    }

    updateEvent(navTree: BaseTreeItem, updatedEvent: EventModel): BaseTreeItem {
        const updatedTree = produce(navTree, (draftState: BaseTreeItem) => {
            draftState.children.forEach((sport) => {
                if (sport.id === LiveUrlParam.Favourites) {
                    sport.children.forEach((league) => {
                        league.events.forEach((event) => {
                            if (event.data.id === updatedEvent.id) {
                                event.data = clone(updatedEvent);
                            }
                        });
                    });
                }
            });
        });

        return updatedTree;
    }

    updateEvents(navTree: BaseTreeItem, events: EventModel[]): BaseTreeItem {
        events.forEach((event) => {
            navTree = this.updateEvent(navTree, event);
        });

        return produce(navTree, (draftState: BaseTreeItem) => {
            events.forEach((event) => {
                const sport = this.findChild(draftState, event.sport.id);
                if (!sport) {
                    return;
                }

                const league = this.findChild(sport, getLeagueId(event));
                if (!league) {
                    return;
                }
                const navEventModels = league.events;
                const index = navEventModels.findIndex((e) => e.data.id === event.id);
                if (index === -1) {
                    return;
                }
                // TODO remove when push update for startdate is implemented
                event.startDate = navEventModels[index].data.startDate;

                navEventModels[index] = {
                    data: clone(event),
                    selected: league.events[index].selected,
                    showMarkets: league.events[index].showMarkets,
                    hidden: league.events[index].hidden,
                };
            });
        });
    }

    toggleSport(navTree: BaseTreeItem, item: TreeItem): ToggleResult {
        const leaguesToFetch: LeagueGroup[] = [];
        const newTree = produce(navTree, (draftState: BaseTreeItem) => {
            const sport = this.findChild(draftState, item.id);
            if (!sport) {
                return;
            }

            sport.expanded = !sport.expanded;

            this.stateService.toggleSport(sport.id);
            this.trackingService.trackSportToggle(sport);

            const leagues = sport.children.filter((l) => l.expanded);
            if (sport.expanded) {
                leagues.forEach((league) => {
                    league.loading = true;
                    leaguesToFetch.push(this.getLeagueGroup(league.id, league.virtualCompetitionId || 0));
                });
            } else {
                leagues.forEach((league) => {
                    this.clearLeagueEvents(league);
                });
            }
        });

        return {
            leaguesToFetch,
            tree: newTree,
        };
    }

    toggleFavourites(navTree: BaseTreeItem, item: TreeItem): BaseTreeItem {
        const newTree = produce(navTree, (draftState: BaseTreeItem) => {
            const sport = this.findChild(draftState, item.id);
            if (!sport) {
                return;
            }

            sport.expanded = !sport.expanded;

            this.stateService.toggleSport(sport.id);
            this.trackingService.trackSportToggle(sport);
        });

        return newTree;
    }

    toggleFavouriteLeague(navTree: BaseTreeItem, item: TreeItem): BaseTreeItem {
        const newTree = produce(navTree, (draft: BaseTreeItem) => {
            let league: TreeItem | undefined;
            for (const sport of draft.children) {
                league = sport.children.find((l) => l.id === item.id && l.type === TreeItemType.FavouriteLeague);
                if (league) {
                    break;
                }
            }

            if (!league) {
                return;
            }

            league.expanded = !league.expanded;

            if (league.expanded) {
                league.events.forEach((event) => (event.hidden = false));
            } else {
                league.events.forEach((event) => (event.hidden = true));
            }

            this.stateService.toggleLeague(league.id);
            this.trackingService.trackLeagueToggle(league, item.leftNavType);
        });

        return newTree;
    }

    toggleLeague(navTree: BaseTreeItem, item: TreeItem): ToggleResult {
        const leaguesToFetch: LeagueGroup[] = [];
        const newTree = produce(navTree, (draft: BaseTreeItem) => {
            let league: TreeItem | undefined;
            for (const sport of draft.children) {
                league = sport.children.find((l) => l.id === item.id && l.type !== TreeItemType.FavouriteLeague);
                if (league) {
                    break;
                }
            }

            if (!league) {
                return;
            }

            league.expanded = !league.expanded;

            this.stateService.toggleLeague(league.id);
            this.trackingService.trackLeagueToggle(league, item.leftNavType);

            if (league.expanded) {
                league.loading = true;
                leaguesToFetch.push(this.getLeagueGroup(league.id, league.virtualCompetitionId || 0));
            } else {
                this.clearLeagueEvents(league);
            }
        });

        return {
            leaguesToFetch,
            tree: newTree,
        };
    }

    refreshTree(oldTree: BaseTreeItem, newTree: BaseTreeItem, groupedEvents: GroupedEvents, updateEvents?: boolean): BaseTreeItem {
        const newSports: TreeItem[] = [];

        const favouriteItem = oldTree.children.find((child) => child.type === TreeItemType.Favourite);
        if (favouriteItem) {
            newSports.push(favouriteItem);
        }

        const liveNavEvens: EventModel[] = [];

        // loads the tree with events
        newTree.children
            .filter((child) => child.type !== TreeItemType.Favourite)
            .forEach((sport) => {
                const currentSport = oldTree.children.find((s) => s.id === sport.id);
                const marketsToggled = currentSport ? currentSport.toggleMarkets : sport.toggleMarkets;

                const newLeagues: TreeItem[] = [];

                // goes through all sport's leagues
                sport.children.forEach((league) => {
                    let leagueEvents: NavEventModel[] = [];

                    if (league.expanded && sport.expanded) {
                        const newEvents = groupedEvents[league.id] || [];
                        const oldLeague = currentSport ? currentSport.children.find((l) => l.id === league.id) : undefined;

                        leagueEvents = this.getNewEvents(newEvents, oldLeague, marketsToggled, updateEvents);
                        liveNavEvens.push(...leagueEvents.map((event) => event.data));
                    }

                    newLeagues.push({
                        ...league,
                        events: leagueEvents,
                    });
                });

                const newSport = {
                    ...sport,
                    toggleMarkets: marketsToggled,
                    children: newLeagues,
                };
                newSports.push(newSport);
            });

        const favouritesEvents = this.getFavouritesEvents();
        this.subscriptionsService.refreshSubscriptions(favouritesEvents.concat(liveNavEvens));

        if (this.favouritesConfig.isLiveFavouritesEnabled && this.userService.isAuthenticated) {
            return this.updateFavouritedItemsLeftNavigation({ children: newSports });
        }

        return {
            children: newSports,
        };
    }

    selectEvent(navTree: BaseTreeItem, event: EventModel): BaseTreeItem {
        this.stateService.storeSelection(new SelectionInfo(event));

        return produce(navTree, (draftState: BaseTreeItem) => {
            if (this.selectionInfo) {
                // unselect
                this.applySelection(this.selectionInfo, false, draftState);
            }

            // select
            this.setSelectionInfo(event);
            this.applySelection(this.selectionInfo, true, draftState);
        });
    }

    setSelectionInfo(selectedEvent: EventModel): void {
        this.selectionInfo = new SelectionInfo(selectedEvent);
    }

    isSelected(eventId: string): boolean {
        return this.selectionInfo.eventId === eventId;
    }

    getFirstLeagueId(tree: BaseTreeItem): number {
        return (tree.children[0] && tree.children[0].children[0] && tree.children[0].children[0].id) || -1;
    }

    getFirstSportId(tree: BaseTreeItem): number {
        return (tree.children[0] && tree.children[0].id) || -1;
    }

    getFavouriteTab(): TreeItem {
        return {
            id: LiveUrlParam.Favourites,
            name: this.sitecore.favouritesMessages.LiveTitle,
            type: TreeItemType.Favourite,
            selected: false,
            expanded: true,
            loading: false,
            icon: 'theme-favorite',
            count: sumBy(this.favouritedLeagues, (league) => league.events.length) || 0,
            children: this.favouritedLeagues || [],
            class: 'sport',
            events: [],
            toggleMarkets: !this.stateService.state.areMarketsCollapsed(LiveUrlParam.Favourites),
            collapsible: true,
            leftNavType: LeftNavType.Live,
            hidden: false,
        };
    }

    setFavouriteLeagues(): void {
        const result: TreeItem[] = [];

        if (this.liveFavouriteFixturesService.liveFixtures.length) {
            const favouriteFixtures: FixtureList[] = this.liveFavouriteFixturesService.getFixtureList(true);

            favouriteFixtures.map((favouriteFix) => {
                if (favouriteFix && favouriteFix.fixtures && favouriteFix.fixtures.length > 0) {
                    const favouritedLiveEvents = this.favouritedLiveEvents(favouriteFix);
                    const leagueId = favouriteFix.fixtures[0].competition.parentLeagueId || favouriteFix.fixtures[0].competition.id;
                    const eventIds = favouritedLiveEvents.map(({ data }) => data.id);
                    result.push({
                        id: leagueId,
                        name: favouriteFix.fixtures[0].competition.name.value.toString(),
                        type: TreeItemType.FavouriteLeague,
                        selected: this.selectionInfo && this.selectionInfo.leagueId === leagueId && eventIds.indexOf(this.selectionInfo.eventId) > -1,
                        expanded: true,
                        loading: false,
                        icon: 'sport-icon sports-' + favouriteFix.fixtures[0].sport.id,
                        count: favouritedLiveEvents.length,
                        children: [],
                        class: 'league',
                        events: favouritedLiveEvents,
                        toggleMarkets: false,
                        collapsible: true,
                        leftNavType: LeftNavType.Live,
                        hidden: false,
                    });
                }
            });
        }
        this.favouritedLeagues = result;
    }

    private favouritedLiveEvents(fixtureList: FixtureList): NavEventModel[] {
        return fixtureList.fixtures
            .filter((model) => model.stage === FixtureStage.Live)
            .map((model) => ({
                data: this.fixtureFactory.create(model),
                selected: this.selectionInfo && model.id.toString() === this.selectionInfo.eventId,
                showMarkets: !this.stateService.state.areMarketsCollapsed(LiveUrlParam.Favourites),
                hidden: false,
                favourited: true,
            }));
    }

    private applySelection(selection: SelectionInfo, value: boolean, state: BaseTreeItem): void {
        if (this.favouritesConfig.isLiveFavouritesEnabled && this.userService.isAuthenticated) {
            this.setSelection(state, LiveUrlParam.Favourites, selection.leagueId, selection.eventId, value);
        }

        this.setSelection(state, selection.sportId, selection.leagueId, selection.eventId, value);
    }

    private setSelection(tree: BaseTreeItem, sportId: number, leagueId: number, eventId: string, value: boolean): void {
        const sport = this.findChild(tree, sportId);
        if (!sport) {
            return;
        }

        const league = this.findChild(sport, leagueId);
        if (!league) {
            return;
        }
        const event = this.findEvent(league, eventId);
        if (!event) {
            return;
        }

        if (!event.hidden) {
            league.selected = value;
        }

        event.selected = value;
    }

    private getNewEvents(
        events: EventModel[],
        currentLeague: TreeItem | undefined,
        marketsToggled?: boolean,
        updateEvents?: boolean,
    ): NavEventModel[] {
        const output: NavEventModel[] = [];

        events.forEach((event) => {
            let oldEvent: NavEventModel | undefined;
            if (currentLeague) {
                oldEvent = currentLeague.events.find((e) => e.data.id === event.id.toString());
            }

            if (oldEvent && oldEvent.data.startDate.getTime() === event.startDate.getTime() && !updateEvents) {
                // if the old event exists, take that to not trigger change detection
                output.push(oldEvent);

                return;
            }

            output.push({
                data: event,
                selected: event.id === this.selectionInfo.eventId,
                showMarkets: marketsToggled,
                hidden: this.liveFavouriteFixturesService.isFavourited(event.id),
            });
        });

        return output;
    }

    private clearLeagueEvents(league: TreeItem): void {
        const nonFavouriteEvents = league.events.filter((event) => !this.liveFavouriteFixturesService.isFavourited(event.data.id));
        this.subscriptionsService.unsubscribe(nonFavouriteEvents.map((e) => e.data));
        league.events = [];
    }

    private findChild(item: BaseTreeItem, id: number): TreeItem | undefined {
        return item.children.find((child) => child.id === id);
    }

    private findEvent(item: TreeItem, id: string): NavEventModel | undefined {
        return item.events.find((child) => child.data.id === id);
    }

    private unsubscribeInvisibleEvents(tree: BaseTreeItem): void {
        const unSubscribeEvents: EventModel[] = [];
        const visibleLeagues = this.getVisibleLeagues(tree);
        tree.children.forEach((sport) => {
            if (sport.id === LiveUrlParam.Favourites) {
                sport.children.forEach((league) => {
                    if (visibleLeagues.indexOf(this.getLeagueGroup(league.id, league.virtualCompetitionId || 0)) === -1) {
                        league.events.forEach((event) => {
                            unSubscribeEvents.push(event.data);
                        });
                    }
                });
            }
        });
        this.subscriptionsService.unsubscribe(unSubscribeEvents);
    }

    private getLeagueGroup(leagueId: number, virtualLeagueId: number): LeagueGroup {
        const leagueGroup: LeagueGroup = {
            leagueId,
            virtualLeagueId,
        };

        return leagueGroup;
    }
}
